'use client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useAxios } from '@/hooks';
import { setLocationPopup } from '@/redux/actions/common.action';
import { setLocation } from '@/redux/actions/location.action';
import { AppDispatch, RootState } from '@/redux/store';
import { endPoint, requestUserLocation } from '@/utils';
import { Loader2, MapPin, Navigation, Search, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cities } from './data';
import { useSearchAddress } from './hooks';
import { cn } from '@/lib';

export default function LocationDialog(): React.ReactNode {
  const [searchQuery, setSearchQuery] = useState('');
  const dispatch = useDispatch<AppDispatch>();
  const { loading, fetchData } = useAxios();
  const { data, handleAddressSelect, handleSearch } = useSearchAddress();
  const location = useSelector((state: RootState) => state.location);

  const isLocationDialogOpen = useSelector(
    (state: RootState) => state.common.isLocationPopupOpen,
  );

  const onOpenChange = () => {
    dispatch(setLocationPopup(false));
    setSearchQuery('');
  };

  const handleCitySelect = (selectedCity: any) => {
    dispatch(setLocation({
      city: selectedCity?.city,
      state: selectedCity?.state,
      lat: selectedCity?.coordinates?.lat,
      lng: selectedCity?.coordinates?.lng,
    }));
    onOpenChange();
  };

  const handleCurrentLocation = async () => {
    try {
      const { latitude, longitude } = await requestUserLocation();
      const locationData = await fetchData({
        url: endPoint.FETCH_COORDINATE_DETAILS,
        params: {
          lat: latitude,
          lng: longitude,
        },
      });

      dispatch(setLocation({
        // `city`, not `location` - the latter is a [lng, lat] pair here.
        city: locationData?.data?.city || locationData?.data?.locality,
        state: locationData?.data?.state || 'Location',
        lat: latitude,
        lng: longitude,
      }));
    } catch (error) {
      console.error(error);
    } finally {
      onOpenChange();
    }
  };

  const handlePopularCitySelect = (city: any) => {
    dispatch(setLocation(city));
    onOpenChange();
  }

  useEffect(() => {
    requestUserLocation().catch(() => {});
  }, [location]);

  return (
    <Dialog open={isLocationDialogOpen} onOpenChange={onOpenChange}>
      <DialogContent className='flex w-full max-w-[full] h-[90%] sm:h-[80%] md:h-min flex-col gap-0 rounded-t-2xl md:rounded-2xl p-0 md:max-w-3xl [&>button:last-child]:text-white overflow-auto hide-scrollbar'>
        <div className="flex flex-col bg-white">
          <DialogHeader className="p-6 pb-4 text-left space-y-1">
            <DialogTitle className="text-xl font-bold tracking-tight text-zinc-900">
              Select your location
            </DialogTitle>
          </DialogHeader>

          <div className='px-6'>
            <div className='relative flex items-center gap-2 md:gap-4 w-full border-[1.5px] rounded-lg px-4 bg-white'>
              <Search
                className='text-muted-foreground'
                size={24}
              />
              <div className='h-5 w-[1px] bg-gray-300' />
              <Input
                placeholder='Search for events, categories...'
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  handleSearch(e.target.value);
                }}
                className='border-none outline-none p-0 focus-visible:ring-0 font-medium text-sm'
              />
              <div className='bg-gray-200 rounded-full p-1' onClick={() => setSearchQuery('')}>
                <X className='text-muted-foreground' size={12} />
              </div>
            </div>
          </div>
        </div>

        <div className="sm:max-h-[60vh] overflow-y-auto px-6 py-6 space-y-8">

          {searchQuery.length > 0 ? (
            <div className="space-y-1">
              <span className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3 block">
                Search Results
              </span>

              {data?.length > 0 ? (
                data?.map((address: any, index: number) => (
                  <button
                    key={index}
                    onClick={() => {
                      handleAddressSelect(address?.placeId, handleCitySelect);
                      setSearchQuery('');
                    }}
                    className="cursor-pointer w-full flex items-center gap-3 p-3 rounded-lg hover:bg-zinc-50 transition-colors text-left group"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-zinc-400 group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                      <MapPin className="h-4 w-4" />
                    </div>
                    <span className="text-sm font-medium text-zinc-700 group-hover:text-zinc-900">
                      {address?.description}
                    </span>
                  </button>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-zinc-500 gap-2">
                  <MapPin className="h-8 w-8 opacity-20" />
                  <p className="text-sm">No cities found matching "{searchQuery}"</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-8">
              <section>
                <button
                  onClick={handleCurrentLocation}
                  disabled={loading}
                  className="w-full cursor-pointer flex items-center gap-4 p-4 rounded-xl border border-primary/10 bg-gradient-to-r from-primary/5 to-transparent"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    {loading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Navigation className="h-5 w-5 -ml-0.5 mt-0.5 fill-current" />
                    )}
                  </div>
                  <div className="flex flex-col items-start text-left">
                    <span className="text-sm font-bold text-primary">Use Current Location</span>
                    <span className="text-xs text-zinc-500 font-medium">
                      {loading ? 'Fetching GPS...' : 'Using GPS'}
                    </span>
                  </div>
                </button>
              </section>

              <section>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">
                    Popular Cities
                  </span>
                  <div className="h-px flex-1 bg-zinc-500/10"></div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {cities.map((city, index) => {
                    const isSelected = city?.city === location?.city;

                    return (
                      <button
                        key={index}
                        className={cn("relative flex flex-col items-center justify-center gap-2 p-3 rounded-lg border border-zinc-200 bg-zinc-50/50 cursor-pointer", isSelected && 'border-primary/10 bg-gradient-to-r from-primary/5 to-transparent')}
                        onClick={() => handlePopularCitySelect(city)}
                      >
                        <span className={cn("text-xs font-semibold text-black", isSelected && 'text-primary')}>
                          {city?.city}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </section>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
